import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Record } from 'immutable';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';

// Utils
import { isActionItem } from 'Shared/utils/MeetingItem.utils';

// Actions
import * as meetingItemActions from 'Shared/actions/MeetingItems.actions';

// Components
import WpUserInput from 'Wpds/components/user-input/WpUserInput';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpAvatar from 'Wpds/components/avatar/WpAvatar';

// Styles
import './AssignOwnerDropdown.scss';

type Props = {
  meetingItem: Record<MeetingItem>;
  selectedUserCustomLabel?: string;
};

const AssignOwnerDropdown = ({ meetingItem, selectedUserCustomLabel }: Props) => {
  const dispatch = useDispatch();

  const uuid = MeetingItemHelpers.getUuid(meetingItem);
  const itemType = MeetingItemHelpers.getItemType(meetingItem);
  const workspaceId = MeetingItemHelpers.getWorkspaceId(meetingItem);
  const assignee = MeetingItemHelpers.getAssignee(meetingItem);
  const assigneeId = assignee.get('userId');
  const usersFromWorkspace = useSelector(state => state.workspaces.find(({ id }) => id === workspaceId)?.users || []);

  const isActionItemType = isActionItem(itemType);

  const handleUpdateAssignee = useCallback(
    assignee => {
      return dispatch(meetingItemActions.updateAssignee(uuid, assignee.id));
    },
    [dispatch, uuid]
  );

  const handleRemoveAssignee = useCallback(() => {
    return dispatch(meetingItemActions.updateAssignee(uuid, null));
  }, [dispatch, uuid]);

  const baseClassName = 'AssignOwnerDropdown';

  const customUserInputMarkdown = ({ user }) => {
    if (!user) {
      return (
        <WpButton size="sm" variant="tertiary">
          <WpIcon name="user" size="sm" />
          {isActionItemType ? 'Add Owner' : 'Add Discussion Leader'}
        </WpButton>
      );
    }
    return (
      <WpButton size="sm" variant="tertiary">
        <div className={`${baseClassName}__owner`}>
          <div className={`${baseClassName}__owner__avatar`}>
            <WpAvatar avatar={user.avatar} size="sm" />
          </div>
          <span className={`${baseClassName}__owner__name`}>
            {selectedUserCustomLabel ? selectedUserCustomLabel : user.label}
          </span>
        </div>
      </WpButton>
    );
  };
  return (
    <WpUserInput
      users={usersFromWorkspace}
      onChange={handleUpdateAssignee}
      handleRemoveUser={handleRemoveAssignee}
      removeUserLabel="Unassign"
      placeholder={isActionItemType ? 'Add Owner' : 'Add Discussion Leader'}
      id="actionItemOwner"
      label="Owner"
      value={assigneeId}
      labelHidden
      components={{ innerDropdown: customUserInputMarkdown }}
    />
  );
};

export default AssignOwnerDropdown;
